import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { ApIcon, colors } from '@alixpartners/ui-components';

import { NavigationWrapper } from '../NavigationWrapper';
import { MappingCancelDialog } from '../../../components/MappingCancelDialog';
import { useAppDispatch, useAppSelector } from '../../../state';
import {
  selectFilesState,
  fetchAllFiles,
  refetchAllFiles,
} from '../../../state/slices/filesSlice';
import {
  ACCOUNTS_PAYABLE,
  ACCOUNTS_RECEIVABLES,
  INVENTORY,
  MAPPING,
  STAGED,
  TFileSection,
} from '../../../types';
import { removeSession, removeSessionFromFxr } from '../../../utils/api';
import { getFxrLink } from '../../../utils/common';

import heroBg from '../../../assets/images/dashboard-hero-bg.png';
import dashboardIllustration from '../../../assets/images/dashboard-hero-illustration.svg';
import aiSparkleIcon from '../../../assets/images/icon-ai-sparkle.svg';

import {
  PageContent,
  HeroCard,
  HeroIllustration,
  HeroTextContent,
  HeroTitle,
  HeroDescription,
  KeyBenefitsGroup,
  KeyBenefitsLabel,
  PillsRow,
  InfoPill,
  AddFilesButton,
  UpdateReportsButton,
  AiBanner,
  AiBannerHeaderRow,
  AiBannerContent,
  AiBannerIcon,
  AiBannerText,
  AiBannerTextStrong,
  AiBannerChevron,
  AiBannerTable,
  AiBannerTableHeaderRow,
  AiBannerTableHeaderCellTitle,
  AiBannerTableHeaderCellDimensions,
  AiBannerTableHeaderCellDate,
  AiBannerTableHeaderCellAction,
  AiBannerTableDataRow,
  AiBannerTableCellTitle,
  AiBannerTableCellText,
  AiBannerTableCellDate,
  AiBannerTableActionsCell,
  AiBannerActionButton,
  AiBannerDeleteButton,
  Card,
  CardHeader,
  CardTitleGroup,
  CardHeading,
  CardSubtitle,
  CardSubtitleSm,
  ButtonRow,
  TableWrapper,
  TableHeaderRow,
  TableHeaderCellFileName,
  TableHeaderCellType,
  TableHeaderCellCreatedBy,
  TableHeaderCellCreatedOn,
  TableHeaderCellActions,
  TableDataRow,
  FileNameCell,
  FileIconContainer,
  TableCellText,
  TableCellType,
  TableCellCreatedBy,
  TableCellCreatedOn,
  TableActionsCell,
  TableEmptyRow,
  RowIconButton,
} from './Dashboard.styles';

const SECTION_DISPLAY_NAMES: Record<TFileSection, string> = {
  'accounts-payable': 'Accounts Payable',
  'accounts-receivables': 'Accounts Receivable',
  inventory: 'Inventory',
};

const KEY_BENEFITS = [
  'Handle multiple files at once',
  'Consistent, standardized outputs',
  'Seamless publishing to Reports',
  'Always up-to-date reporting',
];

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { userFiles } = useAppSelector(selectFilesState);

  useEffect(() => {
    dispatch(fetchAllFiles());
  }, []);

  const mappingSessionsList = [
    ACCOUNTS_PAYABLE,
    ACCOUNTS_RECEIVABLES,
    INVENTORY,
  ].flatMap((section) =>
    Object.keys(userFiles[section]?.[MAPPING] ?? {}).map((sessionId) => {
      const files = userFiles[section][MAPPING][sessionId];
      const firstFile = files[0];
      return {
        section,
        sessionId,
        fileName: firstFile?.name ?? '',
        userName: firstFile?.userName ?? '',
        uploadDate: firstFile?.uploadDate ?? '',
      };
    })
  );

  const mappingsInProgress = mappingSessionsList.length;

  const stagedRows = [
    ACCOUNTS_PAYABLE,
    ACCOUNTS_RECEIVABLES,
    INVENTORY,
  ].flatMap((section) =>
    Object.values(userFiles[section]?.[STAGED] ?? {}).flatMap((files) =>
      files.map((file) => file)
    )
  );

  const [isExpanded, setIsExpanded] = useState(true);
  const [pendingDeleteSessionId, setPendingDeleteSessionId] = useState<
    string | null
  >(null);

  const handleViewDetails = (section: TFileSection, sessionId: string) => {
    window.open(getFxrLink(section, sessionId), '_self');
  };

  const handleDeleteMappingSession = async (sessionId: string) => {
    await removeSession(sessionId);
    await removeSessionFromFxr(sessionId);
    dispatch(refetchAllFiles());
  };

  const handleAddFiles = () => {
    navigate('/data-manager/upload-mapping');
  };

  return (
    <>
      <NavigationWrapper />
      <PageContent>
        <HeroCard bgImage={heroBg}>
          <HeroIllustration src={dashboardIllustration} alt="" />
          <HeroTextContent>
            <HeroTitle>Welcome to Working Capital.</HeroTitle>
            <HeroDescription>
              This tool helps you transform raw financial data (such as bank
              statements or working capital inputs) into structured,
              report-ready datasets. By uploading your files, the system
              suggests how to map and structures the data into a standardized
              format ready for reporting and analysis. Once mapping is complete,
              you can publish them directly to the Reports tab.
            </HeroDescription>
          </HeroTextContent>
          <KeyBenefitsGroup>
            <KeyBenefitsLabel>KEY BENEFITS</KeyBenefitsLabel>
            <PillsRow>
              {KEY_BENEFITS.map((benefit) => (
                <InfoPill key={benefit}>
                  <ApIcon
                    iconName="info_outline"
                    iconColor={colors.white}
                    iconSize={18}
                  />
                  {benefit}
                </InfoPill>
              ))}
            </PillsRow>
          </KeyBenefitsGroup>
        </HeroCard>

        {mappingsInProgress > 0 && (
          <AiBanner>
            <AiBannerHeaderRow onClick={() => setIsExpanded((prev) => !prev)}>
              <AiBannerContent>
                <AiBannerIcon src={aiSparkleIcon} alt="" />
                <AiBannerText>
                  You have{' '}
                  <AiBannerTextStrong>
                    {mappingsInProgress}{' '}
                    {mappingsInProgress === 1 ? 'mapping' : 'mappings'} in
                    progress.
                  </AiBannerTextStrong>
                </AiBannerText>
              </AiBannerContent>
              <AiBannerChevron isExpanded={isExpanded}>
                <ApIcon iconName="keyboard_arrow_down" iconColor="#4a4a4a" />
              </AiBannerChevron>
            </AiBannerHeaderRow>
            {isExpanded && (
              <AiBannerTable>
                <AiBannerTableHeaderRow>
                  <AiBannerTableHeaderCellTitle>
                    File name
                    <ApIcon
                      iconName="unfold_more"
                      material
                      iconColor="#4a4a4a"
                      iconSize="16px"
                    />
                  </AiBannerTableHeaderCellTitle>
                  <AiBannerTableHeaderCellDimensions>
                    File type
                    <ApIcon
                      iconName="unfold_more"
                      material
                      iconColor="#4a4a4a"
                      iconSize="16px"
                    />
                  </AiBannerTableHeaderCellDimensions>
                  <AiBannerTableHeaderCellDate>
                    Started by / Date
                    <ApIcon
                      iconName="unfold_more"
                      material
                      iconColor="#4a4a4a"
                      iconSize="16px"
                    />
                  </AiBannerTableHeaderCellDate>
                  <AiBannerTableHeaderCellAction>
                    Actions
                  </AiBannerTableHeaderCellAction>
                </AiBannerTableHeaderRow>
                {mappingSessionsList.map(
                  ({ section, sessionId, fileName, userName, uploadDate }) => (
                    <AiBannerTableDataRow key={sessionId}>
                      <AiBannerTableCellTitle>
                        {fileName}
                      </AiBannerTableCellTitle>
                      <AiBannerTableCellText>
                        {SECTION_DISPLAY_NAMES[section]}
                      </AiBannerTableCellText>
                      <AiBannerTableCellDate>
                        {userName.includes('@')
                          ? userName.split('@')[0]
                          : userName}{' '}
                        on {moment(uploadDate).format('MM/DD/YYYY')}
                      </AiBannerTableCellDate>
                      <AiBannerTableActionsCell>
                        <AiBannerActionButton
                          onClick={() => handleViewDetails(section, sessionId)}
                        >
                          <ApIcon
                            iconName="visibility"
                            material
                            iconColor="#4a4a4a"
                            iconSize="20px"
                          />
                        </AiBannerActionButton>
                        <AiBannerDeleteButton
                          onClick={() => setPendingDeleteSessionId(sessionId)}
                        >
                          <ApIcon
                            iconName="delete"
                            iconColor="#4a4a4a"
                            iconSize={20}
                          />
                        </AiBannerDeleteButton>
                      </AiBannerTableActionsCell>
                    </AiBannerTableDataRow>
                  )
                )}
              </AiBannerTable>
            )}
          </AiBanner>
        )}

        <Card>
          <CardTitleGroup>
            <CardHeading>Upload your files to get started.</CardHeading>
            <CardSubtitle>
              You&apos;ll go through a quick mapping step to review the data
              before publishing updating your reports
            </CardSubtitle>
          </CardTitleGroup>
          <ButtonRow>
            <AddFilesButton iconName="add" onClick={handleAddFiles}>
              Add your files
            </AddFilesButton>
          </ButtonRow>
        </Card>

        <Card>
          <CardHeader>
            <CardTitleGroup>
              <CardHeading>Data ready for reports</CardHeading>
              <CardSubtitleSm>
                Push your latest mapped data to reports. This will replace the
                existing version.
              </CardSubtitleSm>
            </CardTitleGroup>
            <UpdateReportsButton
              onClick={() => navigate('/data-manager/completed-mappings')}
            >
              Update reports
            </UpdateReportsButton>
          </CardHeader>
          <TableWrapper>
            <TableHeaderRow>
              <TableHeaderCellFileName>File Name</TableHeaderCellFileName>
              <TableHeaderCellType>File type</TableHeaderCellType>
              <TableHeaderCellCreatedBy>Created by</TableHeaderCellCreatedBy>
              <TableHeaderCellCreatedOn>Created on</TableHeaderCellCreatedOn>
              <TableHeaderCellActions>Actions</TableHeaderCellActions>
            </TableHeaderRow>
            {stagedRows.length === 0 && (
              <TableEmptyRow>No mapped data available.</TableEmptyRow>
            )}
            {stagedRows.map((row) => (
              <TableDataRow key={row.id}>
                <FileNameCell>
                  <FileIconContainer>
                    <ApIcon
                      iconName="outline_insert_drive_file"
                      iconColor={colors.grey5}
                    />
                  </FileIconContainer>
                  <TableCellText>{row.name}</TableCellText>
                </FileNameCell>
                <TableCellType>
                  {SECTION_DISPLAY_NAMES[row.section]}
                </TableCellType>
                <TableCellCreatedBy>{row.userName}</TableCellCreatedBy>
                <TableCellCreatedOn>
                  {moment(row.uploadDate).format('MM/DD/YYYY')}
                </TableCellCreatedOn>
                <TableActionsCell>
                  <RowIconButton
                    onClick={() => setPendingDeleteSessionId(row.sessionId)}
                  >
                    <ApIcon
                      iconName="delete"
                      iconColor="#cb282e"
                      iconSize={20}
                    />
                  </RowIconButton>
                </TableActionsCell>
              </TableDataRow>
            ))}
          </TableWrapper>
        </Card>
      </PageContent>
      <MappingCancelDialog
        isOpen={!!pendingDeleteSessionId}
        onClose={() => setPendingDeleteSessionId(null)}
        onCancel={async () => {
          if (pendingDeleteSessionId) {
            await handleDeleteMappingSession(pendingDeleteSessionId);
          }
          setPendingDeleteSessionId(null);
        }}
      />
    </>
  );
};

export default Dashboard;
